(() => {
  "use strict";

  const ready = (callback) => {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", callback, { once: true });
    } else {
      callback();
    }
  };

  const setupCountrySelector = () => {
    const selector = document.querySelector("[data-gw-country-selector]");

    if (!selector || !selector.form) return;

    const additionalCountries = selector.form.querySelector("[data-gw-additional-countries]");
    const revealAdditionalCountries = () => {
      if (!additionalCountries || additionalCountries.dataset.gwLoaded === "true") return;
      selector.appendChild(additionalCountries.content.cloneNode(true));
      additionalCountries.dataset.gwLoaded = "true";
    };

    selector.addEventListener("pointerdown", revealAdditionalCountries, { once: true, passive: true });
    selector.addEventListener("focus", revealAdditionalCountries, { once: true, passive: true });

    selector.addEventListener("change", () => {
      if (typeof selector.form.requestSubmit === "function") {
        selector.form.requestSubmit();
      } else {
        selector.form.submit();
      }
    });
  };

  const setupConsultationJourney = () => {
    const consultation = document.querySelector("[data-gw-consultation-field]");
    const preferredDate = document.querySelector("[data-gw-preferred-date]");
    const alternateDate = document.querySelector("[data-gw-alternate-date]");
    const requestedStyle = document.querySelector("[data-gw-style-name]");
    const matchForm = document.querySelector("[data-gw-match-form]");

    if (matchForm) {
      matchForm.addEventListener("submit", () => {
        matchForm.action = `${matchForm.action.split("#")[0]}#gw-consultation-form`;
      });
    }

    if (!consultation) return;

    if (preferredDate) {
      const today = new Date();
      const localToday = new Date(today.getTime() - today.getTimezoneOffset() * 60000);
      preferredDate.min = localToday.toISOString().slice(0, 10);
      if (alternateDate) alternateDate.min = preferredDate.min;
    }

    const query = new URLSearchParams(window.location.search);
    const selectedConsultation = query.get("consultation");
    if (requestedStyle && query.get("style")) requestedStyle.value = query.get("style");

    if (
      selectedConsultation &&
      Array.from(consultation.options).some((option) => option.value === selectedConsultation)
    ) {
      consultation.value = selectedConsultation;
    }

    const chosenDetails = [];

    document.querySelectorAll("[data-gw-preference]").forEach((field) => {
      const preference = field.dataset.gwPreference;
      const value = query.get(preference);

      if (!value) return;

      field.value = value;
      chosenDetails.push(value);
    });

    const preferenceSummary = document.querySelector("[data-gw-preference-summary]");

    if (preferenceSummary && chosenDetails.length) {
      preferenceSummary.textContent = `Your preferences: ${chosenDetails.join(" · ")}`;
      preferenceSummary.hidden = false;
    }

    document.querySelectorAll("[data-gw-consultation]").forEach((card) => {
      card.addEventListener("click", () => {
        consultation.value = card.dataset.gwConsultation;
      });
    });
  };

  const setupMatchRecommendations = () => {
    const form = document.querySelector("[data-gw-match-form]");
    const results = document.querySelector("[data-gw-match-results]");

    // Shopify can replace a section with cloned markup while the page is open.
    // A data attribute survives that clone, but the event listener does not, so
    // use an element property as the binding guard and keep the data attribute
    // only as a diagnostic marker.
    if (!form || !results || form.gwMatchReady === true) return;

    const cards = Array.from(results.querySelectorAll("[data-gw-match-card]"));
    if (!cards.length) return;

    form.gwMatchReady = true;
    form.dataset.gwMatchReady = "true";

    form.addEventListener("submit", (event) => {
      event.preventDefault();

      const preferences = new FormData(form);
      const requestedColour = String(preferences.get("colour") || "").toLowerCase();
      const requestedTexture = String(preferences.get("texture") || "").toLowerCase();
      const requestedLength = String(preferences.get("length") || "").toLowerCase();
      const requestedBudget = String(preferences.get("budget") || "").toLowerCase();
      const tone = requestedColour.includes("blonde")
        ? "blonde"
        : requestedColour.includes("highlight")
          ? "highlights"
          : requestedColour.includes("brunette")
            ? "brunette"
            : requestedColour.includes("black")
              ? "black"
              : "";
      const texture = requestedTexture.includes("straight")
        ? "straight"
        : requestedTexture.includes("wave")
          ? "wave"
          : "";
      const length = requestedLength.includes("short") || requestedLength.includes("bob")
        ? "short"
        : requestedLength.includes("long")
          ? "long"
          : "";

      const budgetScore = (card) => {
        const price = Number(card.dataset.gwPrice || 0) / 100;
        if (!price || !requestedBudget || requestedBudget.includes("flexible")) return 0;
        if (requestedBudget.includes("under")) return price < 2000 ? 4 : -4;
        if (requestedBudget.includes("2,000")) return price >= 2000 && price <= 2750 ? 4 : -2;
        if (requestedBudget.includes("2,750")) return price > 2750 ? 4 : -2;
        return 0;
      };

      const rankedCards = cards
        .map((card, index) => ({
          card,
          index,
          score:
            (tone && card.dataset.gwTones.split(" ").includes(tone) ? 8 : 0) +
            (texture && card.dataset.gwTexture === texture ? 2 : 0) +
            (length && card.dataset.gwLength === length ? 1 : 0) +
            budgetScore(card),
        }))
        .sort((first, second) => second.score - first.score || first.index - second.index)
        .slice(0, 3);

      cards.forEach((card) => {
        card.hidden = true;
      });

      rankedCards.forEach(({ card }) => {
        const image = card.querySelector("[data-gw-image-src]");
        if (image && !image.getAttribute("src")) image.src = image.dataset.gwImageSrc;
        card.hidden = false;
      });

      const chosenDetails = [
        preferences.get("colour"),
        preferences.get("texture"),
        preferences.get("length"),
        preferences.get("budget"),
      ].filter(Boolean);
      const summary = results.querySelector("[data-gw-match-summary]");

      if (summary) {
        summary.textContent = chosenDetails.length
          ? `For your ${chosenDetails.join(" · ").toLowerCase()} look—here is where I would start.`
          : "Three beautiful starting points. Renée can help you make every detail yours.";
      }

      const consultationLink = results.querySelector("[data-gw-match-consultation]");

      if (consultationLink) {
        const consultationUrl = new URL(form.action, window.location.origin);
        preferences.forEach((value, key) => {
          if (value) consultationUrl.searchParams.set(key, String(value));
        });
        consultationUrl.searchParams.set(
          "style",
          rankedCards.map(({ card }) => card.dataset.gwTitle).join(", ")
        );
        consultationUrl.hash = "gw-consultation-form";
        consultationLink.href = consultationUrl.toString();
      }

      results.hidden = false;
      const heading = results.querySelector("#gw-match-results-title");
      if (heading) heading.focus({ preventScroll: true });
      results.scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
        block: "start",
      });
    });
  };

  const ensureNewArrivalNavigation = () => {
    const drawer = document.querySelector("#t4s-menu-drawer");
    if (!drawer || drawer.querySelector("[data-gw-mobile-arrivals]")) return;

    const shopAnchor = Array.from(drawer.querySelectorAll("a")).find(
      (anchor) => anchor.textContent.trim().toUpperCase() === "SHOP WIGS"
    );
    const shopItem = shopAnchor?.closest("li, .t4s-menu-item");
    if (!shopAnchor || !shopItem || !shopItem.parentElement) return;

    const item = shopItem.cloneNode(false);
    const link = shopAnchor.cloneNode(false);
    item.removeAttribute("id");
    item.classList.remove("has--children", "menu-has__offsets");
    item.dataset.gwMobileArrivals = "true";
    link.textContent = "NEW ARRIVALS";
    link.href = shopAnchor.href.replace(/\/collections\/wigs(?:\/)?(?:\?.*)?$/, "/collections/new-arrivals");
    link.removeAttribute("aria-expanded");
    item.appendChild(link);
    shopItem.insertAdjacentElement("afterend", item);
  };

  const setupAccessibleSwatches = () => {
    document.querySelectorAll("[data-swatch-option]").forEach((group) => {
      const list = group.querySelector(".t4s-swatch__list");
      const title = group.querySelector(".t4s-swatch__title");
      const swatches = Array.from(group.querySelectorAll("[data-swatch-item][data-value]"));

      if (!list || !swatches.length) return;

      list.setAttribute("role", "radiogroup");

      if (title) {
        list.setAttribute("aria-label", title.textContent.split(":")[0].trim());
      }

      const syncSelectedState = () => {
        swatches.forEach((swatch) => {
          const selected = swatch.classList.contains("is--selected");
          swatch.setAttribute("role", "radio");
          swatch.setAttribute("aria-checked", String(selected));
          swatch.setAttribute("aria-label", swatch.dataset.value);
          swatch.tabIndex = selected ? 0 : -1;
        });
      };

      syncSelectedState();

      const selectedStateObserver = new MutationObserver(syncSelectedState);
      swatches.forEach((swatch) => {
        selectedStateObserver.observe(swatch, {
          attributes: true,
          attributeFilter: ["class"],
        });
      });

      swatches.forEach((swatch, index) => {
        swatch.addEventListener("click", () => requestAnimationFrame(syncSelectedState));
        swatch.addEventListener("keydown", (event) => {
          if (event.key === " " || event.key === "Enter") {
            event.preventDefault();
            swatch.click();
            return;
          }

          const forward = event.key === "ArrowRight" || event.key === "ArrowDown";
          const backward = event.key === "ArrowLeft" || event.key === "ArrowUp";

          if (!forward && !backward) return;

          event.preventDefault();

          const offset = forward ? 1 : -1;
          const next = swatches[(index + offset + swatches.length) % swatches.length];
          next.click();
          next.focus();
        });
      });
    });
  };

  const labelStorefrontControls = () => {
    document.querySelectorAll("button[data-quantity-selector]:not([aria-label])").forEach((button) => {
      const label = button.hasAttribute("data-decrease-qty") ? "Decrease quantity" : "Increase quantity";
      button.setAttribute("aria-label", label);
    });

    document.querySelectorAll("button.t4s-drawer__tabs-item:not([aria-label])").forEach((button) => {
      const label = button.dataset.title?.trim();

      if (label) button.setAttribute("aria-label", label);
    });
  };

  const normalizeLengthFilters = () => {
    const facets = document.querySelectorAll("#t4s-filter-hidden .t4s-facet");

    facets.forEach((facet) => {
      const title = facet.querySelector(".t4s-facet-title");

      if (!title || title.textContent.trim().toLowerCase() !== "length") return;

      const groups = new Map();

      facet.querySelectorAll("li").forEach((item) => {
        const link = item.querySelector("a");
        const count = item.querySelector(".t4s-value-count");

        if (!link) return;

        const rawLabel = link.textContent.replace(count?.textContent || "", "").trim();
        const canonical = rawLabel.replace(/["“”″']/g, "").trim();

        if (!/^\d+$/.test(canonical)) return;

        const parsedCount = Number((count?.textContent || "").replace(/\D/g, "")) || 0;
        const current = groups.get(canonical) || [];
        current.push({ item, link, count, parsedCount });
        groups.set(canonical, current);
      });

      groups.forEach((items, length) => {
        if (!items.length) return;

        const active = items.find((entry) => entry.item.classList.contains("is--selected"));
        const primary = active || items[0];
        const primaryUrl = new URL(primary.link.href, window.location.origin);
        const matchingKeys = new Set();
        const matchingValues = new Set();

        items.forEach(({ item, link }) => {
          const url = new URL(link.href, window.location.origin);

          url.searchParams.forEach((value, key) => {
            if (/filter\..*length/i.test(key)) {
              matchingKeys.add(key);
              matchingValues.add(value);
            }
          });

          if (item !== primary.item) {
            item.hidden = true;
            item.setAttribute("aria-hidden", "true");
          }
        });

        if (!active && matchingKeys.size === 1) {
          const key = Array.from(matchingKeys)[0];
          primaryUrl.searchParams.delete(key);
          matchingValues.forEach((value) => primaryUrl.searchParams.append(key, value));
          primary.link.href = `${primaryUrl.pathname}${primaryUrl.search}${primaryUrl.hash}`;
        }

        const labelNode = Array.from(primary.link.childNodes).find(
          (node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim()
        );

        if (labelNode) labelNode.textContent = `${length}″`;

        if (primary.count) {
          primary.count.textContent = `(${items.reduce((sum, entry) => sum + entry.parsedCount, 0)})`;
        }
      });
    });
  };

  const setupSmartFilters = () => {
    document.querySelectorAll(".gw-smart-filters").forEach((section) => {
      if (section.dataset.gwFiltersReady === "true") return;

      section.dataset.gwFiltersReady = "true";
      const filters = Array.from(section.querySelectorAll(".gw-smart-filter"));

      filters.forEach((filter) => {
        filter.addEventListener("toggle", () => {
          if (!filter.open) return;

          filters.forEach((other) => {
            if (other !== filter) other.open = false;
          });
        });
      });

      document.addEventListener("click", (event) => {
        if (section.contains(event.target)) return;
        filters.forEach((filter) => {
          filter.open = false;
        });
      });

      section.addEventListener("keydown", (event) => {
        if (event.key !== "Escape") return;

        const openFilter = filters.find((filter) => filter.open);

        if (!openFilter) return;

        openFilter.open = false;
        openFilter.querySelector("summary")?.focus();
      });
    });
  };

  const setupLazyVideos = () => {
    const videos = document.querySelectorAll("[data-gw-lazy-video]");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (!videos.length || reducedMotion || !("IntersectionObserver" in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(({ target, isIntersecting }) => {
          if (isIntersecting) {
            target.play().catch(() => {});
          } else {
            target.pause();
          }
        });
      },
      { rootMargin: "120px 0px", threshold: 0.1 }
    );

    videos.forEach((video) => observer.observe(video));
  };

  const setupConsiderateWelcome = () => {
    const minimumDelay = 32000;
    const engagedDelay = 12000;
    const startedAt = Date.now();
    const protectedJourney = /\/(?:products\/|pages\/contact(?:\/|$)|cart(?:\/|$)|checkouts?(?:\/|$))/i.test(
      window.location.pathname
    );
    let popupPreviouslyShown = false;

    try {
      popupPreviouslyShown = window.sessionStorage.getItem("gw-welcome-seen") === "true";
    } catch {
      popupPreviouslyShown = false;
    }
    let popupReleased = false;
    let updateQueued = false;

    const replaceHighlightLanguage = () => {
      document
        .querySelectorAll(
          '[class*="ifs"] button, [class*="ifs"] strong, [class*="instagram"] button, [class*="instagram"] strong'
        )
        .forEach((element) => {
          if (element.children.length && element.tagName !== "STRONG") return;

          const original = element.textContent;
          const revised = original.replace(/\bunits\b/gi, "Wigs").replace(/\bunit\b/gi, "Wig");

          if (revised !== original) element.textContent = revised;
        });

      document.querySelectorAll('[aria-label*="Unit"], [title*="Unit"]').forEach((element) => {
        if (!element.closest('[class*="ifs"], [class*="instagram"]')) return;

        ["aria-label", "title"].forEach((attribute) => {
          const current = element.getAttribute(attribute);

          if (current) {
            element.setAttribute(
              attribute,
              current.replace(/\bunits\b/gi, "Wigs").replace(/\bunit\b/gi, "Wig")
            );
          }
        });
      });
    };

    const cookiePromptVisible = () => {
      const prompt = document.querySelector('#shopify-pc__banner, [role="alertdialog"]');

      if (!prompt) return false;

      const style = window.getComputedStyle(prompt);
      return style.display !== "none" && style.visibility !== "hidden" && prompt.getClientRects().length > 0;
    };

    const findWelcome = () =>
      document.querySelector(
        '[role="dialog"][aria-label="POPUP Form"], [role="dialog"][aria-label*="popup" i]'
      );

    const brandWelcome = (dialog) => {
      if (!dialog) return;

      dialog.querySelectorAll("p").forEach((paragraph) => {
        if (!/personally curated by renée|first access to new arrivals/i.test(paragraph.textContent)) return;

        const revised =
          "Receive thoughtful wig guidance, first access to new arrivals and 10% off your first order. Chosen by you. Guided by Renée.";

        if (paragraph.textContent !== revised) paragraph.textContent = revised;
      });
    };

    const holdWelcome = (dialog) => {
      if (!dialog) return;

      let root = dialog;

      while (root.parentElement && root.parentElement !== document.body) root = root.parentElement;

      if (root.matches(".t4s-website-wrapper, #MainContent, main")) root = dialog;

      root.dataset.gwPopupHeld = "true";

      document.body.classList.remove("klaviyo-prevent-body-scrolling");
      if (document.body.style.overflow === "hidden") document.body.style.removeProperty("overflow");
      if (document.documentElement.style.overflow === "hidden") {
        document.documentElement.style.removeProperty("overflow");
      }
    };

    const releaseWelcomeIfAppropriate = () => {
      const dialog = findWelcome();
      brandWelcome(dialog);

      if (popupReleased && !dialog) {
        document.body.classList.remove("klaviyo-prevent-body-scrolling");
        if (document.body.style.overflow === "hidden") document.body.style.removeProperty("overflow");
        if (document.documentElement.style.overflow === "hidden") {
          document.documentElement.style.removeProperty("overflow");
        }
        return;
      }

      if (protectedJourney || popupPreviouslyShown) {
        holdWelcome(dialog);
        return;
      }

      const elapsed = Date.now() - startedAt;
      const scrollableDistance = Math.max(document.documentElement.scrollHeight - window.innerHeight, 1);
      const hasEngaged = window.scrollY / scrollableDistance >= 0.18;
      const timingIsRight = elapsed >= minimumDelay || (hasEngaged && elapsed >= engagedDelay);

      if (!timingIsRight || cookiePromptVisible()) {
        holdWelcome(dialog);
        return;
      }

      popupReleased = true;
      document.documentElement.classList.remove("gw-popup-pending");
      document.querySelectorAll('[data-gw-popup-held="true"]').forEach((element) => {
        delete element.dataset.gwPopupHeld;
      });

      if (dialog) {
        document.body.classList.add("klaviyo-prevent-body-scrolling");
        try {
          window.sessionStorage.setItem("gw-welcome-seen", "true");
        } catch {
          // Privacy-focused browsers can disable session storage.
        }
      }
    };

    const scheduleUpdate = () => {
      if (updateQueued) return;

      updateQueued = true;
      window.requestAnimationFrame(() => {
        updateQueued = false;
        labelStorefrontControls();
        ensureNewArrivalNavigation();
        releaseWelcomeIfAppropriate();
      });
    };

    const observer = new MutationObserver((mutations) => {
      if (!mutations.some((mutation) => mutation.addedNodes.length || mutation.removedNodes.length)) {
        return;
      }
      scheduleUpdate();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.setTimeout(scheduleUpdate, engagedDelay);
    window.setTimeout(scheduleUpdate, minimumDelay);
    document.addEventListener("shopify:section:load", scheduleUpdate);
    replaceHighlightLanguage();
    scheduleUpdate();
  };

  ready(() => {
    setupCountrySelector();
    setupConsultationJourney();
    setupMatchRecommendations();
    ensureNewArrivalNavigation();
    setupAccessibleSwatches();
    labelStorefrontControls();
    normalizeLengthFilters();
    setupSmartFilters();
    setupLazyVideos();
    setupConsiderateWelcome();

    document.addEventListener("shopify:section:load", () => {
      setupAccessibleSwatches();
      labelStorefrontControls();
      normalizeLengthFilters();
      setupSmartFilters();
      setupMatchRecommendations();
      ensureNewArrivalNavigation();
    });
  });
})();
